import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import UniformAPI from '../../firebase/apis/api-uniforms';

const PendingUniformOrdersCard = () => {
  const navigate = useNavigate();
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPendingCount = async () => {
      try {
        const summary = await UniformAPI.getUniformsSummary();
        setPendingCount(summary?.notReceived || 0);
      } catch (error) {
        console.error('Error fetching pending uniform orders count:', error);
        setPendingCount(0);
      } finally {
        setLoading(false);
      }
    };

    fetchPendingCount();

    // Set up real-time updates by polling every 30 seconds
    const interval = setInterval(fetchPendingCount, 30000);

    // Listen for immediate uniform status updates
    const handleUniformUpdate = () => {
      console.log('🔄 Immediate uniform update triggered');
      fetchPendingCount();
    };

    // Custom event listener for immediate updates
    window.addEventListener('uniformStatusChanged', handleUniformUpdate);

    return () => {
      clearInterval(interval);
      window.removeEventListener('uniformStatusChanged', handleUniformUpdate);
    };
  }, []);

  const handleCardClick = () => {
    navigate('/admin/uniform-management', { 
      state: { filter: 'not_received' } 
    });
  };

  if (loading) {
    return (
      <div className="cursor-pointer rounded-2xl p-6 mb-6 lg:mb-8 shadow-md animate-pulse">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 bg-gray-200 rounded-full"></div>
          <div className="flex-1">
            <div className="h-4 bg-gray-200 rounded mb-2 w-32"></div>
            <div className="h-8 bg-gray-200 rounded w-24"></div>
          </div>
        </div>
      </div>
    );
  }

  const hasPendingOrders = pendingCount > 0;
  const cardClasses = `cursor-pointer rounded-2xl p-6 mb-6 lg:mb-8 shadow-md hover:shadow-lg hover:-translate-y-1 transition-all duration-300 flex items-center justify-between ${
    hasPendingOrders 
      ? 'bg-gradient-to-r from-orange-50 to-red-50 border-2 border-orange-200 hover:border-orange-300' 
      : 'bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 hover:border-green-300'
  }`;

  const iconClasses = `p-3 rounded-full ${
    hasPendingOrders ? 'bg-orange-100 text-orange-600' : 'bg-green-100 text-green-600'
  }`;

  const titleClasses = `text-sm font-semibold uppercase tracking-wide ${
    hasPendingOrders ? 'text-orange-700' : 'text-green-700'
  }`;

  const countClasses = `text-3xl lg:text-4xl font-extrabold ${
    hasPendingOrders ? 'text-orange-800' : 'text-green-800'
  }`;

  const subtitleClasses = `text-sm mt-1 ${
    hasPendingOrders ? 'text-orange-600' : 'text-green-600'
  }`;

  const badgeClasses = `px-4 py-2 rounded-full text-sm font-bold ${
    hasPendingOrders 
      ? 'bg-orange-500 text-white hover:bg-orange-600' 
      : 'bg-green-500 text-white hover:bg-green-600'
  }`;

  return (
    <div onClick={handleCardClick} className={cardClasses}>
      <div className="flex items-center gap-4">
        <div className={iconClasses}>
          <span className="text-2xl">👕</span>
        </div>
        <div>
          <div className={titleClasses}>
            Uniform Orders
          </div>
          <div className={countClasses}>
            {hasPendingOrders 
              ? `Pending Uniform Orders: ${pendingCount}` 
              : 'No Pending Uniform Orders'
            }
          </div>
          <div className={subtitleClasses}>
            {hasPendingOrders 
              ? 'Click to view and distribute' 
              : 'All uniforms have been distributed'
            }
          </div>
        </div>
      </div>
      <div className={badgeClasses}>
        {hasPendingOrders ? '⚠️ Action Required' : '✅ All Clear'}
      </div>
    </div>
  );
};

export default PendingUniformOrdersCard;
